import { JobDetourActions } from '@/components/organisms/jobs/JobDetourActions';
import { useInventoryEnabled } from '@/features/purchasing/visibility';
import { JobContactsCard } from '@/components/organisms/jobs/JobContactsCard';
import { JobLocationContactsCard } from '@/components/organisms/jobs/JobLocationContactsCard';
import { JobNotifyChips } from '@/components/organisms/jobs/JobNotifyChips';
import { CustomerContactActions } from '@/components/organisms/jobs/CustomerContactActions';
import { JobVisitDocuments } from '@/components/organisms/jobs/JobVisitDocuments';
import { VisitSchedulingPanel } from '@/components/organisms/jobs/visits/VisitSchedulingPanel';
import { useState, type ElementType, type ReactNode } from 'react';
import {
  createFileRoute,
  getRouteApi,
  Link,
  useLocation,
  useRouter,
  Outlet,
  useChildMatches,
} from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  Clock,
  ExternalLink,
  FilePlus2,
  FileText,
  FolderKanban,
  Layers,
  Loader2,
  Lock,
  MessageSquareText,
  MoreVertical,
  Pencil,
  MoveRight,
  Play,
  Receipt,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import { normalizeJob } from '@/lib/normalizeJob';
import {
  canCreateEstimateFromJob,
  estimateFromJobSearch,
  isServiceCallJob,
  jobKindLabel,
  proposalFromJobSearch,
} from '@/lib/jobs/serviceCall';
import { isJobBillingReadOnly } from '@/lib/jobs/jobBillingAccess';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { ScheduleTimezoneScope, useScheduleTimezone } from '@/lib/time/scheduleTimezone';
import { IrisAttributionBadge } from '@/components/molecules/IrisAttributionBadge';
import { JobPartsBadge } from '@/features/purchasing/JobPartsBadge';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import {
  useHasAnyPermission,
  useHasPermission,
  useIsFieldHidden,
  usePermissionScope,
} from '@/hooks/usePermissions';
import { useAuthTechnicianId } from '@/stores/auth.store';
import { canHoldJob, canRestoreJob } from '@/lib/jobs/jobHoldAuthority';
import { cn, formatLabel as sharedFormatLabel } from '@/lib/utils';
import {
  hasJobLineItems,
  START_JOB_REQUIRES_LINE_ITEMS_MESSAGE,
} from '@/lib/jobs/jobScopeValidation';
import { useContact } from '@/hooks/contacts/useContact';
import { getContactDisplayName } from '@/lib/contacts';
import { useJobPipelines } from '@/hooks/pipelines/useJobPipelines';
import { useJobPipeline } from '@/hooks/pipelines/useJobPipeline';
import { useTransitionJob } from '@/hooks/pipelines/useTransitionJob';
import { useJobAppointments } from '@/hooks/jobs/useJobAppointments';
import { useCreateProjectFromJob } from '@/hooks/projects/useProjects';
import { JobRelatedStrip } from '@/components/organisms/jobs/JobRelationshipsCard';
import { useScheduleJob } from '@/hooks/jobs/useScheduleJob';
import { useRestoreJob } from '@/hooks/jobs/useRestoreJob';
import { useJobInvoices } from '@/hooks/invoices/useInvoices';
import {
  findCurrentPipelineStage,
  getJobDetourOptions,
  getNearestPathStageBefore,
  getNextPipelinePathStage,
  getPipelinePathStages,
  isDetourPipelineStage,
  isTerminalPipelineStage,
  normalizePipelinePhase,
  sortPipelineStages,
  stageCoreStatusLabel,
  TERMINAL_PIPELINE_PHASES,
} from '@/lib/jobs/jobPipeline';
import {
  requestPipelineWorkflowOverride,
  type PipelineWorkflowOverridePayload,
} from '@/lib/jobs/pipelineWorkflowOverride';
import {
  JobAppointments,
  type OpenVisitOptions,
} from '@/components/organisms/jobs/JobAppointments';
import { getAppointmentTechnicians } from '@/components/organisms/jobs/support/appointment-technicians';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { BillingDetailDrawerProvider } from '@/components/organisms/jobs/billing/BillingDetailDrawer';
import {
  RecurringJobDetail,
  RECURRING_JOB_TABS,
  RECURRING_CONTENT_WIDTH,
  type RecurringJobTab,
} from '@/components/organisms/jobs/recurring/RecurringJobDetail';
import { InternalChatPanel } from '@/components/internal-communications/InternalChatPanel';
import { JobReportsTab } from '@/components/organisms/jobs/JobReportsTab';
import { JobNoteAttachments } from '@/components/organisms/jobs/JobNoteAttachments';
import { EntityNotes } from '@/components/organisms/notes/EntityNotes';
import { SalesDocumentNotesDocsSection } from '@/components/organisms/sales/SalesDocumentDetailSections';
import { EntityCustomFieldsSection } from '@/components/organisms/custom-fields/EntityCustomFieldsSection';
import { TagSelector } from '@/components/organisms/TagSelector';
import { RecurringActivityTab } from '@/components/organisms/jobs/recurring/RecurringActivityTab';
import { jobPriceDisplay } from '@/components/organisms/jobs/recurring/recurringDetail.helpers';
import { useUpdateJob } from '@/hooks/jobs/useUpdateJob';
import { JobCloseBlockedDialog } from '@/components/organisms/jobs/JobCloseBlockedDialog';
import {
  formatBillingAddress,
  formatServiceAddress,
  savedLocationName,
} from '@/components/organisms/jobs/support/job-contact-format';
import {
  appointmentScheduleAnchor,
  formatScheduledDay,
  hasScheduledAppointment,
} from '@/components/organisms/jobs/support/appointment-schedule';
import { getPendingRescheduleRequest } from '@/components/organisms/jobs/support/visit-status-copy';
import { getJobNextStepBanner } from '@/components/organisms/jobs/support/job-next-step-banner';
import {
  invoiceCoveredByClearing,
  jobInvoiceBalance,
  summarizeJobInvoiceClearing,
} from '@/components/organisms/jobs/support/job-finance-labels';
import { findTechnicianByAssignmentId } from '@/lib/scheduler/technicianAssignment';
import {
  JobPipelineStrip,
  type JobPipelineStripStage,
} from '@/components/organisms/jobs/JobPipelineStrip';
import {
  type JobDetailRailGateState,
  type JobDetailRailProps,
} from '@/components/organisms/jobs/JobDetailRail';
import { OneOffJobRail } from '@/components/organisms/jobs/OneOffJobRail';
import { NewVisitDrawer } from '@/components/organisms/jobs/NewVisitDrawer';
import { requiredMinutesForAddedWork } from '@/components/organisms/jobs/support/added-visit-fit';
import { DueIncludedVisitsCard } from '@/components/organisms/memberships/DueIncludedVisitsCard';
import { NewExpenseDrawer } from '@/components/organisms/expenses/NewExpenseDrawer';
import { NewInvoiceDrawer } from '@/components/organisms/invoices/NewInvoiceDrawer';
import { PaymentScheduleDrawer } from '@/components/organisms/payment-schedules/PaymentScheduleDrawer';
import { OneOffBillingSummary } from '@/components/organisms/jobs/OneOffBillingSummary';
import { OneOffCostsTab } from '@/components/organisms/jobs/OneOffCostsTab';
import { JobEquipmentSection } from '@/components/organisms/jobs/JobEquipmentSection';
import { JobMaterialsTab } from '@/features/inventory/JobMaterialsTab';
import { JobIrisSurveyCard } from '@/components/organisms/jobs/JobIrisSurveyCard';
import { useJobPortalStatus } from '@/hooks/jobs/useJobPortalStatus';
import type { SalesLifecycleStep } from '@/components/organisms/sales/SalesLifecycleStepper';
import { SectionErrorState } from '@/components/molecules/SectionErrorState';

import { DetailPageLayout } from '@/components/layouts';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import { useBusinessUnitLabel } from '@/components/organisms/business-units/useBusinessUnitLabel';
import {
  isActiveAppointmentsError,
  type ActiveAppointmentSummary,
} from '@/lib/jobs/active-appointments-error';
import type { Job, JobStatusPhase } from '@/types/job';
import type {
  ConfirmationStatus,
  JobAppointment,
  AppointmentTechnician,
  RescheduleRequest,
} from '@/types/jobAppointment';
import type { CorePhase, PipelineStage } from '@/types/pipeline';

// Search params the estimate detail route requires; mirrors the constant used
// elsewhere for estimate-detail links (NOT-246).
const DEFAULT_ESTIMATE_DETAIL_SEARCH = {
  ...DEFAULT_ESTIMATE_LIST_FILTERS,
  tab: 'overview' as const,
};

// The full set of valid `?tab=` values across both job shells: the one-off job
// uses Visits/Finances/Notes; the recurring parent uses its own six tabs
// (RECURRING_JOB_TABS). validateSearch keeps any of them so both are
// deep-linkable; each shell coerces to its own default when the param doesn't
// belong to it.
const JOB_TABS = [
  'overview',
  'checklists',
  'equipment',
  'materials',
  'visits',
  'media',
  'billing',
  'finances',
  'costs',
  'reports',
  'activity',
  'chat',
  'notes',
] as const;
type JobTab = (typeof JOB_TABS)[number];
type PaymentScheduleDrawerMode = 'new' | 'edit';
type JobDetailSearch = {
  tab?: JobTab;
  expenseDrawer?: 'new';
  invoiceDrawer?: 'new';
  paymentScheduleDrawer?: PaymentScheduleDrawerMode;
  appointmentId?: string;
};

const LEGACY_JOB_TAB_MAP: Record<string, JobTab> = {
  appointments: 'visits',
  financials: 'finances',
  payments: 'finances',
  'payment-schedule': 'finances',
};

const NEXT_ACTION_LABELS: Partial<Record<CorePhase, string>> = {
  SCHEDULED: 'Schedule',
  IN_PROGRESS: 'Start',
  COMPLETED: 'Complete',
  INVOICED: 'Mark invoiced',
  CLOSED: 'Close',
};

const NEXT_ACTION_ICONS: Partial<Record<CorePhase, ElementType>> = {
  SCHEDULED: CalendarDays,
  IN_PROGRESS: Play,
  COMPLETED: CheckCircle2,
  INVOICED: FileText,
  CLOSED: Lock,
};

const UNPAID_INVOICE_STATUSES = new Set(['draft', 'open', 'sent', 'viewed', 'partial', 'overdue']);

// Where a Hold / Cancel action moves the job. `id: null` cancels it with no stage:
// a tenant's pipeline can leave out a Cancelled stage, and the job must still be
// cancellable (useTransitionJob sends it to the lifecycle cancel endpoint).
type DetourTarget = Pick<PipelineStage, 'corePhase'> & { id: string | null };
const CANCEL_WITHOUT_STAGE: DetourTarget = { id: null, corePhase: 'CANCELLED' };

// Returns the canonical tab, or undefined when there's no (valid) tab param —
// the default then depends on the job shell (one-off → visits, recurring →
// overview), which validateSearch can't know, so it's resolved in the component.
function normalizeJobTab(value: unknown): JobTab | undefined {
  if (typeof value !== 'string') return undefined;
  if (JOB_TABS.includes(value as JobTab)) return value as JobTab;
  return LEGACY_JOB_TAB_MAP[value];
}

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id')({
  validateSearch: (search): JobDetailSearch => {
    const tab = normalizeJobTab(search.tab);
    return {
      ...(tab ? { tab } : {}),
      ...(search.expenseDrawer === 'new' ? { expenseDrawer: 'new' as const } : {}),
      ...(search.invoiceDrawer === 'new' ? { invoiceDrawer: 'new' as const } : {}),
      ...(search.paymentScheduleDrawer === 'new' || search.paymentScheduleDrawer === 'edit'
        ? {
            paymentScheduleDrawer: search.paymentScheduleDrawer as PaymentScheduleDrawerMode,
          }
        : {}),
      ...(typeof search.appointmentId === 'string' ? { appointmentId: search.appointmentId } : {}),
    };
  },
  component: JobDetailPage,
});

// The jobs list filters/sort/page live on the parent layout route's search. The
// detail reads them so the "‹ Jobs" back link returns to the same filtered list
// instead of a reset index (NOT-297).
const jobsListRouteApi = getRouteApi('/_dashboard/dashboard/jobs');

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string | null, timeZone?: string | null): string {
  if (!iso) return 'date unavailable';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'date unavailable';
  return formatDateTimeInTimeZone(iso, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatLabel(value?: string | null): string {
  return value ? sharedFormatLabel(value) : 'Not set';
}

function formatMoney(value?: number | null): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value ?? 0);
}

// NOT-297: derive the earliest scheduled future appointment for the rail's
// "Next visit" row + its technician name.
function getNextScheduledAppointment(appointments: JobAppointment[]): JobAppointment | null {
  const now = Date.now();
  return (
    appointments
      .filter((appointment) => {
        const anchor = appointmentScheduleAnchor(appointment);
        if (!hasScheduledAppointment(appointment) || !anchor) return false;
        const time = new Date(anchor).getTime();
        return !Number.isNaN(time) && time >= now;
      })
      .sort(
        (a, b) =>
          new Date(appointmentScheduleAnchor(a) as string).getTime() -
          new Date(appointmentScheduleAnchor(b) as string).getTime(),
      )[0] ?? null
  );
}

// NOT-360: the rail must show the WHOLE crew of the relevant visit, not one name.
// Same visit selection as the rail next visit (next scheduled, else most recent
// with a crew); returns every technician (LEAD first via getAppointmentTechnicians).
function getRailCrew(appointments: JobAppointment[]): AppointmentTechnician[] {
  const next = getNextScheduledAppointment(appointments);
  const nextCrew = next ? getAppointmentTechnicians(next) : [];
  if (nextCrew.length > 0) return nextCrew;
  const mostRecentWithCrew = appointments
    .filter(
      (appointment) => getAppointmentTechnicians(appointment).length > 0 && appointment.startAt,
    )
    .sort(
      (a, b) => new Date(b.startAt as string).getTime() - new Date(a.startAt as string).getTime(),
    )[0];
  return mostRecentWithCrew ? getAppointmentTechnicians(mostRecentWithCrew) : [];
}

// NOT-360: order LEAD(s) first, then format for the rail row.
//  0 → "Unassigned"; 1 → the name; 2+ → "Alice (Lead) +N".
function formatRailCrew(crew: AppointmentTechnician[]): string {
  if (crew.length === 0) return 'Unassigned';
  const ordered = [...crew].sort(
    (a, b) => (a.role === 'LEAD' ? 0 : 1) - (b.role === 'LEAD' ? 0 : 1),
  );
  if (ordered.length === 1) return ordered[0]!.name;
  const lead = ordered[0]!;
  const leadLabel = lead.role === 'LEAD' ? `${lead.name} (Lead)` : lead.name;
  return `${leadLabel} +${ordered.length - 1}`;
}

// NOT-297: collapse a {required} policy flag + collectedAt into the rail's
// Not required / Pending / Approved (or Signed) tri-state.
function deriveGateState(
  required: boolean | undefined,
  collectedAt: string | null | undefined,
): JobDetailRailGateState {
  if (!required) return 'not_required';
  return collectedAt ? 'approved' : 'pending';
}

function normalizeLifecycleStatus(value: string | null | undefined): JobStatusPhase {
  const normalized = String(value ?? '').toUpperCase();
  return JOB_LIFECYCLE_STATUSES.has(normalized) ? (normalized as JobStatusPhase) : 'DRAFT';
}

// Where an included membership visit can still ride along — the server's
// EDITABLE_JOB_STATUSES. Past these the job's work is done or closed.
const INCLUDED_VISIT_ATTACHABLE_STATUSES: ReadonlySet<JobStatusPhase> = new Set([
  'DRAFT',
  'SCHEDULED',
  'IN_PROGRESS',
  'ON_HOLD',
]);

const JOB_LIFECYCLE_STATUSES: ReadonlySet<string> = new Set([
  'DRAFT',
  'SCHEDULED',
  'IN_PROGRESS',
  'ON_HOLD',
  'COMPLETED',
  'CANCELLED',
  'INVOICED',
  'CLOSED',
  'ARCHIVED',
]);

const READ_ONLY_JOB_STATUSES: ReadonlySet<JobStatusPhase> = new Set([
  'COMPLETED',
  'CANCELLED',
  'CLOSED',
  'ARCHIVED',
]);

// NOT-297 qa#2: the header Edit / Send actions stay available on COMPLETED jobs
// (per the mockup's Complete state — billing/booking may still need touching),
// but are hidden on genuinely dead states where editing makes no sense.
const HEADER_ACTIONS_HIDDEN_STATUSES: ReadonlySet<JobStatusPhase> = new Set([
  'CANCELLED',
  'CLOSED',
  'ARCHIVED',
]);

function getNextActionLabel(stage: PipelineStage): string {
  return NEXT_ACTION_LABELS[stage.corePhase] ?? `Move to ${stage.name}`;
}

function isTerminalPhase(phase: JobStatusPhase): boolean {
  return TERMINAL_PIPELINE_PHASES.has(phase as CorePhase);
}

function buildJobLifecycleSteps({
  stages,
  pathCurrentStage,
  currentIndex,
  isTerminal,
  lifecycleStatus,
}: {
  stages: PipelineStage[];
  pathCurrentStage: PipelineStage | null;
  currentIndex: number;
  isTerminal: boolean;
  lifecycleStatus: JobStatusPhase;
}): SalesLifecycleStep[] {
  if (stages.length === 0) {
    return [
      {
        key: 'status',
        label: formatLabel(lifecycleStatus),
        state: isTerminal ? 'done' : 'current',
      },
    ];
  }

  return stages.map((stage, index) => {
    const isCurrent = !isTerminal && stage.id === pathCurrentStage?.id;
    const isDone = isTerminal ? currentIndex >= index : currentIndex > index;
    return {
      key: stage.id,
      label: stage.name,
      state: isCurrent ? 'current' : isDone ? 'done' : 'pending',
    };
  });
}

// NOT-297: the job title/number/status now live in the in-page header (see
// JobDetailHeader). The detail opens as a dedicated full page, so the workspace
// (list) header is no longer slotted here — the in-page breadcrumb owns back-nav.

// NOT-297 Wave 1 — full-width in-page header above the main + rail columns.
function JobDetailHeader({
  job,
  readOnly,
  onEdit,
  onCreateVisit,
  widthClassName = 'w-full',
}: {
  job: Job;
  readOnly: boolean;
  onEdit: () => void;
  onCreateVisit?: () => void;
  widthClassName?: string;
}) {
  const sourceEstimate = job.estimateId && job.sourceEstimate ? job.sourceEstimate : null;
  const router = useRouter();
  const canCreateEstimates = useHasPermission('estimates:create');
  const createProjectFromJob = useCreateProjectFromJob();
  const pricingHidden = useIsFieldHidden('PRICING');
  // Completion closes operational editing, but it must not close the sales
  // loop: a diagnostic visit can produce an estimate days after the work was
  // completed. The shared eligibility gate still rejects terminal Jobs.
  const estimateReadOnly =
    readOnly && normalizeLifecycleStatus(job.statusPhase ?? job.status) !== 'COMPLETED';
  const canCreateEstimate = canCreateEstimateFromJob(job, {
    canCreateEstimates,
    pricingHidden,
    readOnly: estimateReadOnly,
  });
  const listSearch = jobsListRouteApi.useSearch();

  return (
    <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      {/* NOT-297/NOT-339: the header's inner row shares the width of the detail
          content below it — full-width for one-off, capped+centered for recurring
          — so their left/right edges line up instead of the header overhanging. */}
      <div className={cn(widthClassName, 'px-5 py-3')}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Link
              to={
                listSearch.serviceCallWorkspace
                  ? '/dashboard/jobs/service-calls'
                  : '/dashboard/jobs'
              }
              search={listSearch}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="size-3.5" />
              {listSearch.serviceCallWorkspace ? 'Service calls' : 'Jobs'} · {job.jobNumber}
            </Link>
            {/* NOT-339 — both shells own their identity (name / status / customer) in the
                content block under the tabs (RecurringJobDetail / OneOffJobDetail), so the
                top bar stays a slim toolbar (breadcrumb + price + actions) for both. The
                title is never duplicated up here. */}
          </div>

          <div className="flex shrink-0 items-center gap-3 lg:justify-end">
            {!pricingHidden &&
              (() => {
                // Billing-mode-aware: a recurring subscription/prepaid job bills its
                // period/term baseAmount ($100 /mo), not the line-item scope grandTotal.
                const price = jobPriceDisplay(job);
                return (
                  <div className="text-left lg:text-right">
                    <p className="text-xl font-semibold tabular-nums leading-tight">
                      {formatMoney(price.amount)}
                      {price.unit && (
                        <span className="ml-0.5 text-sm font-normal text-muted-foreground">
                          {price.unit}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{price.subtitle}</p>
                  </div>
                );
              })()}
            <div className="flex items-center gap-2">
              {!readOnly ? (
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Pencil size={14} />
                  Edit
                </Button>
              ) : null}
              {/* Opens the same empty manual/template picker the Estimates
                  list opens — nothing is written until the operator saves. See
                  estimateFromJobSearch for why the job's lines don't come. */}
              {canCreateEstimate ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void router.navigate({
                      to: '/dashboard/estimates/new',
                      // `as never` mirrors the proposal navigation below —
                      // TanStack merges the parent list search type here, which
                      // we intentionally don't satisfy.
                      search: estimateFromJobSearch(job) as never,
                    })
                  }
                >
                  <FilePlus2 size={14} />
                  Create estimate
                </Button>
              ) : null}
              {/* A Good/Better/Best proposal is an alternative sales path from
                  any eligible one-off Job. The selected option becomes an approved
                  estimate whose explicit destination is resolved independently. */}
              {canCreateEstimate ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void router.navigate({
                      to: '/dashboard/proposals/new',
                      // `as never` mirrors the proposal builder's own
                      // navigations — TanStack merges the parent list search
                      // type here, which we intentionally don't satisfy.
                      search: proposalFromJobSearch(job) as never,
                    })
                  }
                >
                  <Layers size={14} />
                  Create proposal
                </Button>
              ) : null}
              {/* Edit lives only on the dedicated button above — no dup here.
                  Render the ⋮ only when it has ≥1 item: source estimate,
                  project link, or (writable) create-project. (NOT-297.) */}
              {sourceEstimate || job.projectId || !readOnly || onCreateVisit ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon-sm" aria-label="More actions">
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {onCreateVisit && (
                      <DropdownMenuItem onSelect={onCreateVisit}>
                        <CalendarDays size={14} />
                        Create visit
                      </DropdownMenuItem>
                    )}
                    {sourceEstimate ? (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/dashboard/estimates/$id"
                          params={{ id: sourceEstimate.id }}
                          search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
                        >
                          <ExternalLink size={14} />
                          {sourceEstimate.status === 'APPROVED' ||
                          sourceEstimate.status === 'CONVERTED'
                            ? 'Open approved estimate'
                            : `Approval pending · ${sourceEstimate.status.replaceAll('_', ' ')}`}
                        </Link>
                      </DropdownMenuItem>
                    ) : null}
                    {job.projectId ? (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/dashboard/projects/$projectId"
                          params={{ projectId: job.projectId }}
                          search={{ tab: 'overview' } as never}
                        >
                          <FolderKanban size={14} />
                          Open project
                        </Link>
                      </DropdownMenuItem>
                    ) : !readOnly ? (
                      <DropdownMenuItem
                        disabled={createProjectFromJob.isPending}
                        onSelect={() =>
                          createProjectFromJob.mutate(job.id, {
                            onSuccess: (project) => {
                              void router.navigate({
                                to: '/dashboard/projects/$projectId',
                                params: { projectId: project.id },
                                search: { tab: 'overview' } as never,
                              });
                            },
                          })
                        }
                      >
                        <FolderKanban size={14} />
                        Create project from job
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// NOT-297: the customer + address block moved out of the old JobSummaryCard into
// a standalone "Customer" card rendered in the main column. SalesContactBlock /
// SalesAddressBlock are reused as-is (props/data unchanged).

function JobNextStepCard({
  job,
  lifecycleStatus,
  pipelineStages,
  currentStage,
  isPipelineLoading,
  readOnly,
  canManageLifecycle,
  canHoldJob: canHoldJobHere,
  canRestoreJob: canRestoreJobHere,
  billingReadOnly,
  onSchedule,
  onSwitchTab,
  hasScheduledAppointment,
  isScheduleCheckLoading,
  isSchedulePending,
  balanceDue,
  pendingInvoiceCount,
  invoiceCount,
  pricingHidden,
  pendingRescheduleRequest,
  nextVisitConfirmation,
}: {
  job: Job;
  lifecycleStatus: JobStatusPhase;
  pipelineStages: PipelineStage[];
  currentStage: PipelineStage | null;
  isPipelineLoading: boolean;
  readOnly: boolean;
  canManageLifecycle: boolean;
  canHoldJob: boolean;
  canRestoreJob: boolean;
  billingReadOnly: boolean;
  onSchedule?: () => void;
  onSwitchTab: (tab: JobTab) => void;
  hasScheduledAppointment: boolean;
  isScheduleCheckLoading: boolean;
  isSchedulePending: boolean;
  balanceDue: number;
  pendingInvoiceCount: number;
  invoiceCount: number;
  pricingHidden: boolean;
  /** BR-77 — the customer's outstanding reschedule request, if any. */
  pendingRescheduleRequest: RescheduleRequest | null;
  /** `confirmationStatus` of the next scheduled visit, the one the rail shows. */
  nextVisitConfirmation: ConfirmationStatus | null;
}) {
  const transition = useTransitionJob();
  const restore = useRestoreJob();
  const [confirmRestore, setConfirmRestore] = useState(false);
  const companyTimeZone = useCompanyTimezone();
  const [closeBlocked, setCloseBlocked] = useState<{
    operation: 'complete' | 'cancel';
    stageId: string | null;
    appointments: ActiveAppointmentSummary[];
    workflowOverride?: PipelineWorkflowOverridePayload;
  } | null>(null);
  const [cancelBlockingBalance, setCancelBlockingBalance] = useState<{
    stage: DetourTarget;
  } | null>(null);
  const [pendingCompleteWithoutInvoice, setPendingCompleteWithoutInvoice] = useState<{
    stage: PipelineStage;
    workflowOverride?: PipelineWorkflowOverridePayload;
  } | null>(null);
  const unpaidInvoiceLabel =
    Math.max(1, pendingInvoiceCount) === 1
      ? '1 unpaid invoice'
      : `${Math.max(1, pendingInvoiceCount)} unpaid invoices`;
  const showCreateInvoiceCta =
    !pricingHidden && !billingReadOnly && lifecycleStatus === 'COMPLETED' && invoiceCount === 0;
  const autoInvoiceOnComplete = Boolean(job.autoInvoiceOnComplete);
  // NOT-560 / SPEC D3 — diagnostic-fee lifecycle indicator. The fee machine
  // persists explicit state on `job.serviceCallFee`; when present it drives the
  // chip below regardless of job status (a PENDING fee is meaningful while the
  // customer decides). Rows from before the machine (or older backends) have no
  // `serviceCallFee`, so they keep the legacy check: a completed call carrying
  // an `isServiceCallFeeCredit` line means the fee was waived.
  const serviceCallFeeStatus = isServiceCallJob(job) ? (job.serviceCallFee?.status ?? null) : null;
  const legacyDiagnosticFeeWaived =
    isServiceCallJob(job) &&
    job.serviceCallFee == null &&
    lifecycleStatus === 'COMPLETED' &&
    job.lineItems.some((li) => li['isServiceCallFeeCredit'] === true);
  const stages = getPipelinePathStages(pipelineStages);
  const isOnHold = currentStage?.corePhase === 'ON_HOLD' || lifecycleStatus === 'ON_HOLD';
  const isDraft = currentStage?.corePhase === 'DRAFT' || lifecycleStatus === 'DRAFT';
  const isTerminal = isTerminalPhase(lifecycleStatus) || isTerminalPipelineStage(currentStage);
  const pausedFromStage = job.pausedFromStageId
    ? (pipelineStages.find((stage) => stage.id === job.pausedFromStageId) ?? null)
    : null;
  const pathCurrentStage = isOnHold
    ? (pausedFromStage ?? getNearestPathStageBefore(pipelineStages, currentStage))
    : isDetourPipelineStage(currentStage)
      ? getNearestPathStageBefore(pipelineStages, currentStage)
      : currentStage;
  const resumeStage = isOnHold ? pathCurrentStage : null;
  const currentIndex = pathCurrentStage
    ? stages.findIndex((stage) => stage.id === pathCurrentStage.id)
    : -1;
  const primaryStage =
    isOnHold && resumeStage
      ? resumeStage
      : !isTerminal && !isDetourPipelineStage(currentStage)
        ? getNextPipelinePathStage(pipelineStages, currentStage)
        : null;
  const currentStageLabel =
    pathCurrentStage?.name ?? currentStage?.name ?? formatLabel(lifecycleStatus);
  // The Next-step card owns the "Schedule a visit" action only when there's no
  // visit yet — a Draft with nothing scheduled. NOT when resuming an on-hold job
  // (its next stage is the Scheduled it was paused from → keep the Resume CTA), and
  // NOT when a Draft already has a scheduled visit (then the hero must keep its
  // Draft → Scheduled advance button, otherwise there's no way to move the job
  // forward). Scheduling also always lives in the Visits tab; the old always-on
  // identity-header button was removed.
  const isScheduleAction =
    primaryStage?.corePhase === 'SCHEDULED' &&
    Boolean(onSchedule) &&
    !isOnHold &&
    !hasScheduledAppointment;
  const isStartAction = primaryStage?.corePhase === 'IN_PROGRESS';
  const blocksStartWithoutLineItems = isStartAction && !hasJobLineItems(job);
  const actionLabel =
    isOnHold && primaryStage ? 'Resume' : primaryStage ? getNextActionLabel(primaryStage) : null;
  const ActionIcon = isOnHold
    ? Play
    : primaryStage
      ? (NEXT_ACTION_ICONS[primaryStage.corePhase] ?? MoveRight)
      : MoveRight;
  const detourActionPhases: ReadonlySet<CorePhase> =
    isTerminal || readOnly || !canManageLifecycle
      ? new Set<CorePhase>()
      : // A Draft job has nothing to pause yet, and On Hold shouldn't re-hold —
        // both only offer Cancel. (Resuming to Draft also trips a backend outbox gap.)
        isOnHold || isDraft
        ? new Set<CorePhase>(['CANCELLED'])
        : new Set<CorePhase>(canHoldJobHere ? ['ON_HOLD', 'CANCELLED'] : ['CANCELLED']);
  const { stages: detourStages, cancelWithoutStage: offersCancelWithoutStage } =
    getJobDetourOptions({
      stages: pipelineStages,
      currentStageId: currentStage?.id,
      phases: detourActionPhases,
      isLoading: isPipelineLoading,
    });
  const showDetourActions =
    !readOnly && canManageLifecycle && (detourStages.length > 0 || offersCancelWithoutStage);
  const nextCopy = getNextPipelineCopy({
    lifecycleStatus,
    currentStageLabel,
    nextStage: isOnHold ? null : primaryStage,
    hasStages: stages.length > 0,
    isLoading: isPipelineLoading,
    isScheduleAction,
    isOnHold,
    isTerminal,
    hasScheduledAppointment,
    isScheduleCheckLoading,
    terminalDate: lifecycleStatus === 'CANCELLED' ? job.cancelledAt : job.archivedAt,
    fallbackDate: job.updatedAt,
    balanceDue,
    pendingInvoiceCount,
    invoiceCount,
    autoInvoiceOnComplete,
    pricingHidden,
    timeZone: companyTimeZone,
  });
  const lifecycleSteps = buildJobLifecycleSteps({
    stages,
    pathCurrentStage,
    currentIndex,
    isTerminal,
    lifecycleStatus,
  });
  function handleNextStep() {
    if (readOnly || isPipelineLoading) return;
    if (!primaryStage) return;
    if (blocksStartWithoutLineItems) {
      toast.error(START_JOB_REQUIRES_LINE_ITEMS_MESSAGE);
      return;
    }
    if (isScheduleAction && onSchedule) {
      onSchedule();
      return;
    }
    if (!canManageLifecycle) return;
    if (primaryStage.corePhase === 'COMPLETED' && !autoInvoiceOnComplete && invoiceCount === 0) {
      setPendingCompleteWithoutInvoice({ stage: primaryStage });
      return;
    }
    transition.mutate(
      { id: job.id, stageId: primaryStage.id },
      { onError: (error) => handleTransitionError(error, primaryStage) },
    );
  }

  // NOT-531 — clicking a pipeline-strip chip on the Overview moves the job to that
  // stage. It runs the SAME safeguards as the hero next-step button (handleNextStep),
  // keyed on the clicked target stage rather than only the next stage: a Scheduled
  // click with no visit opens the scheduling drawer, Start requires line items, and
  // Complete without an invoice confirms first. Active-appointment blocks reuse the
  // shared close-confirm flow; other failures surface a toast.
  function handleStageSelect(stageId: string) {
    // Also gate on the scheduling state: while the appointment check is loading
    // `hasScheduledVisit` is briefly false (would misroute a Draft-with-visit), and a
    // pending scheduleJob must not be re-enqueued.
    if (
      readOnly ||
      !canManageLifecycle ||
      isPipelineLoading ||
      transition.isPending ||
      isScheduleCheckLoading ||
      isSchedulePending
    ) {
      return;
    }
    const stage = stages.find((candidate) => candidate.id === stageId);
    if (!stage) return;
    if (stage.corePhase === 'ON_HOLD' && !canHoldJobHere) return;
    if (stage.corePhase === 'IN_PROGRESS' && !hasJobLineItems(job)) {
      toast.error(START_JOB_REQUIRES_LINE_ITEMS_MESSAGE);
      return;
    }
    // Advancing a Draft to Scheduled must go through the schedule flow (onSchedule =
    // handleScheduleNextStep): it opens the drawer when there's no visit yet, or seeds
    // scheduledStart/End from the existing visit before transitioning. A backward click
    // to Scheduled (from a later stage) just transitions.
    if (stage.corePhase === 'SCHEDULED' && onSchedule && !isOnHold && lifecycleStatus === 'DRAFT') {
      onSchedule();
      return;
    }
    const workflowOverride = requestPipelineWorkflowOverride({
      stages: pipelineStages,
      currentStageId: currentStage?.id,
      targetStageId: stage.id,
      pausedFromStageId: job.pausedFromStageId,
    });
    if (workflowOverride === null) return;
    if (stage.corePhase === 'COMPLETED' && !autoInvoiceOnComplete && invoiceCount === 0) {
      setPendingCompleteWithoutInvoice({ stage, workflowOverride });
      return;
    }
    transition.mutate(
      { id: job.id, stageId, ...workflowOverride },
      {
        onError: (error) => handleTransitionError(error, stage, workflowOverride),
      },
    );
  }

  function handleDetourAction(stage: DetourTarget) {
    if (readOnly || isPipelineLoading) return;
    if (stage.corePhase === 'ON_HOLD' && !canHoldJobHere) return;
    if (stage.corePhase === 'CANCELLED' && lifecycleStatus === 'COMPLETED' && balanceDue > 0) {
      setCancelBlockingBalance({ stage });
      return;
    }
    transition.mutate(
      { id: job.id, stageId: stage.id },
      { onError: (error) => handleTransitionError(error, stage) },
    );
  }

  function confirmCancelWithBalance() {
    if (!cancelBlockingBalance) return;
    const { stage } = cancelBlockingBalance;
    setCancelBlockingBalance(null);
    transition.mutate(
      { id: job.id, stageId: stage.id },
      { onError: (error) => handleTransitionError(error, stage) },
    );
  }

  function confirmCompleteWithoutInvoice() {
    if (!pendingCompleteWithoutInvoice) return;
    const { stage, workflowOverride } = pendingCompleteWithoutInvoice;
    setPendingCompleteWithoutInvoice(null);
    transition.mutate(
      { id: job.id, stageId: stage.id, ...workflowOverride },
      {
        onError: (error) => handleTransitionError(error, stage, workflowOverride),
      },
    );
  }

  function closeOperationFor(stage: DetourTarget): 'complete' | 'cancel' | null {
    if (stage.corePhase === 'COMPLETED') return 'complete';
    if (stage.corePhase === 'CANCELLED') return 'cancel';
    return null;
  }

  function handleTransitionError(
    error: unknown,
    stage: DetourTarget,
    workflowOverride?: PipelineWorkflowOverridePayload,
  ) {
    const operation = closeOperationFor(stage);
    if (operation && isActiveAppointmentsError(error)) {
      setCloseBlocked({
        operation,
        stageId: stage.id,
        appointments: error.data.details.activeAppointments,
        workflowOverride,
      });
      return;
    }
    toast.error(error instanceof Error ? error.message : 'Failed to update job stage.');
  }

  function handleCascadeClose() {
    if (!closeBlocked) return;
    transition.mutate(
      {
        id: job.id,
        stageId: closeBlocked.stageId,
        cascadeCancelActiveAppointments: true,
        closeOperation: closeBlocked.operation,
        ...closeBlocked.workflowOverride,
      },
      {
        onSuccess: () => setCloseBlocked(null),
        onError: (error) => {
          if (isActiveAppointmentsError(error)) {
            setCloseBlocked({
              ...closeBlocked,
              appointments: error.data.details.activeAppointments,
            });
            return;
          }
          toast.error(`Failed to cancel active visits and ${closeBlocked.operation} the job.`);
        },
      },
    );
  }

  // NOT-297 Wave 2 — map the page's already-derived lifecycle steps into the
  // job-specific strip's done/current/future tri-state (no re-derivation). Uses
  // the tenant's custom stage names already baked into `lifecycleSteps.label`.
  // A skipped step (an office-approved estimate was never sent or viewed) is
  // left out rather than shown as upcoming: the strip knows done/current/
  // future, and "future" would put the send back in front of the reader.
  //
  // Each chip also carries the core job status its stage maps to. A tenant that
  // renamed SCHEDULED to "Dispatched" showed the reader "Dispatched" and nothing
  // else, right above visit rows carrying their own status pills — two states,
  // no stated relationship. `stageCoreStatusLabel` returns null for a stage
  // already named after its phase, so nothing repeats itself.
  const stripStages: JobPipelineStripStage[] = lifecycleSteps
    .filter((step) => step.state !== 'skipped')
    .map((step) => ({
      id: step.key,
      name: step.label,
      state: step.state === 'done' || step.state === 'current' ? step.state : 'future',
      coreStatus: stageCoreStatusLabel(
        stages.find((stage) => stage.id === step.key) ?? { name: step.label },
      ),
    }));

  // Relocated Hold / Cancel detour buttons — rendered in the strip's right slot.
  // Handlers + confirmation dialogs stay owned here (not duplicated).
  const detourRightSlot = showDetourActions ? (
    <JobDetourActions
      stages={detourStages}
      disabled={isPipelineLoading || transition.isPending}
      onSelect={handleDetourAction}
      onCancelWithoutStage={
        offersCancelWithoutStage ? () => handleDetourAction(CANCEL_WITHOUT_STAGE) : undefined
      }
    />
  ) : null;

  // The contextual banner's per-stage text + action. Terminal / on-hold jobs
  // keep the existing status copy (no actionable next step).
  const banner = getJobNextStepBanner({
    corePhase: isTerminal || isOnHold ? null : (pathCurrentStage?.corePhase ?? null),
    fallbackText: nextCopy,
    onSwitchTab,
    pendingRescheduleRequest,
    nextVisitConfirmation,
  });

  return (
    <>
      <JobPipelineStrip
        stages={stripStages}
        currentStateLabel={
          isOnHold
            ? currentStage?.corePhase === 'ON_HOLD'
              ? currentStage.name
              : 'On hold'
            : undefined
        }
        rightSlot={
          <>
            {detourRightSlot}{' '}
            <div className="flex shrink-0 items-center gap-3">
              {lifecycleStatus === 'CANCELLED' && canRestoreJobHere ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setConfirmRestore(true)}
                  disabled={restore.isPending}
                >
                  Restore job
                  {restore.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RotateCcw size={14} />
                  )}
                </Button>
              ) : null}
              {showCreateInvoiceCta ? (
                <Link
                  to="/dashboard/jobs/$id"
                  params={{ id: job.id }}
                  search={{ tab: 'finances' }}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Create invoice →
                </Link>
              ) : null}
              {!readOnly && canManageLifecycle && actionLabel && !isScheduleAction ? (
                blocksStartWithoutLineItems ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex shrink-0">
                        <Button size="sm" className="shrink-0" disabled>
                          {actionLabel}
                          <ActionIcon size={14} />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-72">
                      {START_JOB_REQUIRES_LINE_ITEMS_MESSAGE}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    size="sm"
                    className="shrink-0"
                    onClick={handleNextStep}
                    disabled={
                      isPipelineLoading ||
                      transition.isPending ||
                      isSchedulePending ||
                      (isScheduleAction && isScheduleCheckLoading)
                    }
                  >
                    {actionLabel}
                    {transition.isPending || isSchedulePending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ActionIcon size={14} />
                    )}
                  </Button>
                )
              ) : null}
            </div>
          </>
        }
        onStageSelect={
          !readOnly && canManageLifecycle && stages.length > 0 ? handleStageSelect : undefined
        }
        isPending={transition.isPending || isSchedulePending || isScheduleCheckLoading}
      />
      {(isOnHold || isTerminal || (showCreateInvoiceCta && autoInvoiceOnComplete)) && (
        <p className="text-sm text-muted-foreground">{banner.text}</p>
      )}
      {serviceCallFeeStatus === 'CREDIT_REVERSAL_FLAGGED' ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>A credited diagnostic fee needs review — the approving estimate was voided.</span>
        </div>
      ) : serviceCallFeeStatus === 'PENDING' ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <Clock className="size-3.5 shrink-0" />
          Diagnostic fee pending outcome.
        </p>
      ) : serviceCallFeeStatus === 'CREDITED' ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          Diagnostic fee credited.
        </p>
      ) : serviceCallFeeStatus === 'BILLED' ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <Receipt className="size-3.5 shrink-0" />
          Diagnostic fee billed.
        </p>
      ) : serviceCallFeeStatus === 'CREDIT_MEMO_ISSUED' ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          Fee credited via credit memo.
        </p>
      ) : legacyDiagnosticFeeWaived ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          Diagnostic fee waived — customer proceeded.
        </p>
      ) : null}
      {closeBlocked ? (
        <JobCloseBlockedDialog
          open
          onOpenChange={(open) => {
            if (!open) setCloseBlocked(null);
          }}
          operation={closeBlocked.operation}
          activeAppointments={closeBlocked.appointments}
          onCascade={handleCascadeClose}
          isCascading={transition.isPending}
        />
      ) : null}
      {cancelBlockingBalance ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open) setCancelBlockingBalance(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pricingHidden
                  ? 'Cancel a completed job with unpaid invoices?'
                  : 'Cancel a completed job with outstanding balance?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pricingHidden ? (
                  <>
                    This completed job still has unpaid invoices. Cancelling now keeps those
                    invoices unpaid in the system but flags the job as cancelled.
                  </>
                ) : (
                  <>
                    This job is completed with {formatMoney(balanceDue)} outstanding across{' '}
                    {unpaidInvoiceLabel}. Cancelling now keeps the invoices unpaid in the system but
                    flags the job as cancelled. Usually you'd want to void the unpaid invoices
                    first.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep the job</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmCancelWithBalance}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Cancel job anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
      {pendingCompleteWithoutInvoice ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingCompleteWithoutInvoice(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark this job complete without an invoice?</AlertDialogTitle>
              <AlertDialogDescription>
                No invoice has been created yet, and this job is configured to not auto-invoice on
                completion. You can create an invoice later from the Finances tab.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmCompleteWithoutInvoice}
                disabled={transition.isPending}
              >
                Mark complete anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this job?</AlertDialogTitle>
            <AlertDialogDescription>
              The job will return to Draft. Its cancelled visits and former schedule will stay
              cancelled. Any cancellation fees, refunds, invoices, and estimate changes remain in
              place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep cancelled</AlertDialogCancel>
            <AlertDialogAction
              disabled={restore.isPending}
              onClick={(event) => {
                event.preventDefault();
                restore.mutate(job.id, {
                  onSuccess: () => setConfirmRestore(false),
                  onError: (error) =>
                    toast.error(error instanceof Error ? error.message : 'Could not restore job.'),
                });
              }}
            >
              Restore to Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function getNextPipelineCopy({
  lifecycleStatus,
  currentStageLabel,
  nextStage,
  hasStages,
  isLoading,
  isScheduleAction,
  isOnHold,
  isTerminal,
  hasScheduledAppointment,
  isScheduleCheckLoading,
  terminalDate,
  fallbackDate,
  balanceDue,
  pendingInvoiceCount,
  invoiceCount,
  autoInvoiceOnComplete,
  pricingHidden,
  timeZone,
}: {
  lifecycleStatus: JobStatusPhase;
  currentStageLabel: string;
  nextStage: PipelineStage | null;
  hasStages: boolean;
  isLoading: boolean;
  isScheduleAction: boolean;
  isOnHold: boolean;
  isTerminal: boolean;
  hasScheduledAppointment: boolean;
  isScheduleCheckLoading: boolean;
  terminalDate?: string | null;
  fallbackDate?: string | null;
  balanceDue: number;
  pendingInvoiceCount: number;
  invoiceCount: number;
  autoInvoiceOnComplete: boolean;
  pricingHidden: boolean;
  timeZone?: string | null;
}) {
  if (isLoading) return 'Loading the job pipeline...';
  if (isTerminal) {
    const statusLabel = formatLabel(lifecycleStatus).toLowerCase();
    return `This job was ${statusLabel} on ${formatDate(terminalDate ?? fallbackDate, timeZone)}. No further steps.`;
  }
  if (isOnHold) return `This job is on hold from ${currentStageLabel}.`;
  if (!hasStages) return `This job is in ${currentStageLabel}. No pipeline stages are configured.`;
  if (lifecycleStatus === 'COMPLETED' && invoiceCount === 0) {
    if (pricingHidden) {
      return 'Job completed. No further steps.';
    }
    if (autoInvoiceOnComplete) {
      return "Auto-invoice should have generated for this job but didn't. Create an invoice manually or check the system logs.";
    }
    return "Job completed. Create an invoice when you're ready.";
  }
  if (lifecycleStatus === 'COMPLETED' && balanceDue > 0) {
    if (pricingHidden) {
      return 'Job completed. Awaiting payment. The job will move to Closed when paid in full.';
    }
    const invoiceLabelCount = Math.max(1, pendingInvoiceCount);
    const invoiceLabel = invoiceLabelCount === 1 ? '1 invoice' : `${invoiceLabelCount} invoices`;
    return `Job completed. Awaiting payment — ${formatMoney(balanceDue)} outstanding across ${invoiceLabel}. The job will move to Closed when paid in full.`;
  }
  if (!nextStage) {
    if (lifecycleStatus === 'COMPLETED') {
      return 'This pipeline has no stages after Completed. Add INVOICED and CLOSED stages in Settings → Job Pipelines to track payment progress automatically.';
    }
    return `This job is in ${currentStageLabel}. There is no next pipeline stage configured.`;
  }
  if (isScheduleAction) {
    if (isScheduleCheckLoading) {
      return `This job is in ${currentStageLabel}. Checking existing visits before scheduling.`;
    }
    if (hasScheduledAppointment) {
      return `This job is in ${currentStageLabel}. A visit is already scheduled, so move this job to ${nextStage.name}.`;
    }
    return `This job is in ${currentStageLabel}. Schedule the first visit to move this job to ${nextStage.name}.`;
  }
  return `This job is in ${currentStageLabel}. Move it to ${nextStage.name} to keep the job moving.`;
}

// NOT-297 Wave 4 — whole-page loading shell shown while the top-level job query
// resolves. Mirrors the real layout (sticky header bar + total on the right, a
// 2-col main + rail) with neutral Skeleton bars so the page doesn't flash a
// bare spinner or shift when the job loads. Rail sections match JobDetailRail's
// Job / Schedule / Source footprint (3 rows each).
function JobDetailRailSkeleton() {
  return (
    <>
      {[3, 3, 4].map((rows, sectionIndex) => (
        <section key={sectionIndex} className="rounded-lg border bg-card p-3">
          <Skeleton className="mb-3 h-3 w-16" />
          <div className="space-y-2.5">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div key={rowIndex} className="flex items-center justify-between gap-3">
                <Skeleton className="h-3.5 w-16" />
                <Skeleton className="h-3.5 w-20" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function JobDetailSkeleton() {
  return (
    <DetailPageLayout
      contentClassName="bg-muted/30"
      headerClassName="block border-0 p-0"
      header={
        <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="w-full px-5 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-4 w-40" />
              </div>
              <div className="flex shrink-0 items-center gap-3 lg:justify-end">
                <div className="space-y-1.5 text-right">
                  <Skeleton className="ml-auto h-6 w-28" />
                  <Skeleton className="ml-auto h-3.5 w-20" />
                </div>
                <Skeleton className="h-8 w-16 rounded-md" />
                <Skeleton className="size-8 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      }
      rail={<JobDetailRailSkeleton />}
    >
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-md" />
      <Skeleton className="h-72 w-full rounded-xl" />
    </DetailPageLayout>
  );
}

const ONEOFF_TAB_ITEMS: Array<{ value: RecurringJobTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'checklists', label: 'Forms' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'media', label: 'Media & Notes' },
  { value: 'billing', label: 'Billing' },
  { value: 'costs', label: 'Costs' },
  { value: 'reports', label: 'Reports' },
  { value: 'activity', label: 'Activity' },
  { value: 'chat', label: 'Chat' },
];

/**
 * One-off job detail — the same URL-synced tabbed shell the recurring
 * detail uses (Overview · Visits · Billing · Costs · Activity · Notes), a shared identity
 * header shown once, per-field Custom Fields + Tags, and the split Billing/Costs tabs.
 * The pipeline hero (JobNextStepCard), rail (JobDetailRail) and Visits body are built by
 * the route with its computed props and passed in; everything else is job-id based.
 */
function OneOffJobDetail({
  job,
  activeTab,
  onTabChange,
  readOnly = false,
  billingReadOnly = false,
  hero,
  visits,
  railStage,
  railStageCoreStatus,
  railServiceType,
  railNextVisit,
  railOwner,
  railTech,
  railCrewLabel,
  onOpenCustomer,
  onCreateExpense,
  onCreateInvoice,
  onManagePaymentSchedule,
  pricingHidden,
  profitabilityHidden,
}: {
  job: Job;
  activeTab: RecurringJobTab;
  onTabChange: (tab: RecurringJobTab) => void;
  readOnly?: boolean;
  billingReadOnly?: boolean;
  hero: ReactNode;
  visits: ReactNode;
  railStage: string;
  railStageCoreStatus: string | null;
  railServiceType?: string | null;
  railNextVisit?: string | null;
  railOwner: string;
  railTech: string;
  railCrewLabel?: string;
  onOpenCustomer?: () => void;
  onCreateExpense?: () => void;
  onCreateInvoice?: () => void;
  onManagePaymentSchedule?: (mode: PaymentScheduleDrawerMode) => void;
  pricingHidden: boolean;
  profitabilityHidden: boolean;
}) {
  const inventoryEnabled = useInventoryEnabled();
  const contactQuery = useContact(job.contactId ?? '', {
    enabled: Boolean(job.contactId),
  });
  // The router's own location, rather than reading the `window` global during
  // render — same value, but reactive and pure.
  const jobHref = useLocation({ select: (l) => `${l.pathname}${l.searchStr}` });
  const customerName: string | null =
    (contactQuery.data ? getContactDisplayName(contactQuery.data) : null) ||
    job.billToContact?.contactName ||
    job.billToContact?.name ||
    null;
  const serviceAddressLabel: string | null = formatServiceAddress(job.serviceAddress) || null;
  const serviceLocationName = savedLocationName(
    contactQuery.data?.addresses,
    job.locationId ?? job.serviceAddress?.addressId,
  );
  const billingAddressLabel = job.billingAddress
    ? formatServiceAddress(job.billingAddress)
    : formatBillingAddress(job.billToContact?.billingAddress);
  const billingLocationName = savedLocationName(
    contactQuery.data?.addresses,
    job.billingAddress?.addressId,
  );
  const portalStatus = useJobPortalStatus(job.id).data;
  const billToLabel: string | null =
    job.billToContact?.contactName || job.billToContact?.name || null;
  // NOT-542 — the resolved Business Unit, whether it was inherited from the job
  // type or pinned by a dispatcher. Null for tenants that don't use units, which
  // drops the rail row entirely.
  const { labelFor: businessUnitLabelFor } = useBusinessUnitLabel();
  const businessUnitLabel = businessUnitLabelFor(job.businessUnitId);

  // Inline tag editing — optimistic local state → PATCH { tagIds }, roll back on error
  // (mirrors the recurring rail). Safe useState init: this component only mounts once the
  // job is loaded.
  const updateJob = useUpdateJob();
  const [tagIds, setTagIds] = useState<string[]>(() => job.tags?.map((t) => t.tagId) ?? []);
  const handleTagIdsChange = (next: string[]) => {
    const prev = tagIds;
    setTagIds(next);
    updateJob.mutate(
      { id: job.id, tagIds: next },
      {
        onError: () => {
          setTagIds(prev);
          toast.error('Failed to update tags');
        },
      },
    );
  };

  return (
    <div className="w-full">
      {/* Identity header (shown once) */}
      <div className="mb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <h1
              title={job.name}
              className="min-w-64 flex-1 truncate text-[23px] font-bold leading-tight tracking-tight text-foreground"
            >
              {job.name}
            </h1>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">{jobKindLabel(job)} · One-off</span>
            {job.jobType?.name ? (
              <>
                <span className="text-border">·</span>
                <span className="text-muted-foreground">Job type: {job.jobType.name}</span>
              </>
            ) : null}

            {businessUnitLabel && (
              <>
                <span className="text-border">·</span>
                <span className="text-muted-foreground">Business unit: {businessUnitLabel}</span>
              </>
            )}
            {job.bookedByIris && <IrisAttributionBadge kind="booked" />}
            <JobPartsBadge jobId={job.id} />
            {customerName && (
              <>
                <span className="text-border">·</span>
                {/* NOT-701 — the operator on a job constantly needs the customer
                    behind it. Without a contact id there is nothing to link to,
                    so the name stays plain text rather than rendering a dead
                    link. Carries the same `returnTo` the rail's Open customer
                    action does, so Back lands on this job. */}
                {job.contactId ? (
                  <Link
                    to="/dashboard/contacts/$contactId"
                    params={{ contactId: job.contactId }}
                    search={{ returnTo: jobHref }}
                    className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {customerName}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">{customerName}</span>
                )}
                <CustomerContactActions contact={contactQuery.data} />
                {/* Text and Email say we CAN reach them; these say whether the
                    system WILL, which is a different question and was only
                    answerable in the rail. */}
                <JobNotifyChips jobId={job.id} contactId={job.contactId} />
              </>
            )}
            {serviceAddressLabel && (
              <>
                <span className="text-border">·</span>
                <span className="text-muted-foreground">{serviceAddressLabel}</span>
              </>
            )}
          </div>
          {(!readOnly || tagIds.length > 0) && (
            <div className="mt-3 flex flex-wrap items-center gap-2" aria-label="Job tags">
              <TagSelector
                scope="job"
                variant="inline"
                value={tagIds}
                onChange={handleTagIdsChange}
                canEdit={!readOnly}
              />
            </div>
          )}
        </div>
        {/* No schedule button in the identity header — it showed on every tab.
            Scheduling lives in the Visits tab ("+ Schedule visit"). */}
      </div>

      <section aria-label="Job status" className="mb-5 space-y-3 rounded-xl border bg-card p-4">
        {hero}
      </section>

      {/* Tab bar */}
      <nav className="mb-5 flex max-w-full items-end gap-5 overflow-x-auto whitespace-nowrap border-b">
        {ONEOFF_TAB_ITEMS.filter(
          (item) =>
            (!pricingHidden || item.value !== 'billing') &&
            (!pricingHidden && !profitabilityHidden ? true : item.value !== 'costs') &&
            (inventoryEnabled || item.value !== 'materials'),
        ).map((t) => {
          const active = t.value === activeTab || (t.value === 'media' && activeTab === 'notes');
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => onTabChange(t.value)}
              className={cn(
                'border-b-2 border-transparent pb-3 text-[13.5px] text-muted-foreground transition-colors hover:text-foreground',
                active && 'border-primary font-semibold text-foreground',
              )}
              aria-current={active ? 'page' : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Panels */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_312px]">
          <div className="min-w-0 space-y-5">
            {/* Above the visit list, not below it: the estimate this job was
                sold on is the record staff jump to most, and as a card at the
                foot of the column it sat off screen behind every visit. */}
            <JobRelatedStrip jobId={job.id} pricingHidden={pricingHidden} />
            {visits}
            <EntityCustomFieldsSection
              entityType="job"
              entityId={job.id}
              canEdit={!readOnly}
              emptyDescription="Job-specific custom field values will appear here."
              jobTypeId={job.jobType?.id ?? null}
            />
          </div>
          <div className="space-y-5">
            <OneOffJobRail
              contactsSlot={
                <div className="space-y-5">
                  <JobLocationContactsCard job={job} readOnly={readOnly} />
                  <JobContactsCard job={job} readOnly={readOnly} />
                </div>
              }
              stage={railStage}
              stageCoreStatus={railStageCoreStatus}
              serviceType={railServiceType}
              businessUnit={businessUnitLabel}
              nextVisit={railNextVisit}
              stageSource={job.stageSource}
              completedVisitCount={job.completedVisitCount}
              totalVisitCount={job.totalVisitCount}
              owner={railOwner}
              tech={railTech}
              crewLabel={railCrewLabel}
              customerName={customerName}
              serviceAddress={serviceAddressLabel}
              serviceLocationName={serviceLocationName}
              billingAddress={billingAddressLabel}
              billingLocationName={billingLocationName}
              locationId={job.locationId ?? job.serviceAddress?.addressId}
              billToLabel={billToLabel}
              taxLabel={null}
              portalStatus={portalStatus}
              onOpenCustomer={onOpenCustomer}
            />
          </div>
        </div>
      )}

      {activeTab === 'checklists' && <JobVisitDocuments job={job} />}

      {activeTab === 'billing' && (
        <OneOffBillingSummary
          job={job}
          readOnly={readOnly}
          billingReadOnly={billingReadOnly}
          onCreateInvoice={onCreateInvoice}
          onManagePaymentSchedule={onManagePaymentSchedule}
        />
      )}

      {activeTab === 'costs' && (
        <OneOffCostsTab job={job} readOnly={readOnly} onCreateExpense={onCreateExpense} />
      )}

      {activeTab === 'activity' && (
        <div className="space-y-5">
          <RecurringActivityTab jobId={job.id} enabled={activeTab === 'activity'} />
        </div>
      )}

      {activeTab === 'chat' && <InternalChatPanel jobId={job.id} />}

      {(activeTab === 'media' || activeTab === 'notes') && (
        <div className="space-y-5">
          <SalesDocumentNotesDocsSection
            scope={job.scopeOfWork}
            notes={job.notes}
            notesLabel="Notes"
            terms={job.termsAndConditions}
            allowHtmlScope
            allowHtmlTerms
            showEmptyWhenNoContent={false}
          />
          <EntityNotes
            entityType="JOB"
            entityId={job.id}
            heading="Notes"
            icon={MessageSquareText}
            description="Timestamped job notes — each can carry photos and files — with admin-only, internal, or customer-visible visibility."
            emptyText="No job notes yet."
            framed
          />
          <JobNoteAttachments jobId={job.id} />
        </div>
      )}

      {/* Reports are the polished customer-facing deliverable for this job. Their
          lifecycle is independent of the job's (a report can be created/published
          after the job is completed or closed — see Job Reports spec §A.3), so this
          tab is intentionally NOT gated on the job's read-only status; creation is
          governed by the reports:create permission inside JobReportsTab. */}
      {activeTab === 'reports' && (
        <div className="space-y-5">
          <JobReportsTab jobId={job.id} />
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function JobDetailPage() {
  const { id } = Route.useParams();
  return (
    <ScheduleTimezoneScope key={id}>
      <JobDetailContent />
    </ScheduleTimezoneScope>
  );
}

function JobDetailContent() {
  const router = useRouter();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const listSearch = jobsListRouteApi.useSearch();
  const childMatches = useChildMatches();
  const companyTimeZone = useCompanyTimezone();
  // Display-only Company/My-time preference. Scheduler values remain UTC.
  const scheduleTz = useScheduleTimezone();
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const customerFinancialsHidden = useIsFieldHidden('CUSTOMER_FINANCIALS');
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  // The Materials tab is inventory (PLAT-FF-1): a link to it opens Overview
  // for a company without it.
  const materialsEnabled = useInventoryEnabled();
  const canScheduleJob = useHasPermission('jobs:edit');
  const jobEditScope = usePermissionScope('jobs:edit');
  const actorTechnicianId = useAuthTechnicianId();
  const canCreateVisit = useHasAnyPermission('jobs:edit', 'jobs:create_visit');
  const [newVisitOpen, setNewVisitOpen] = useState(false);
  const [newVisitSource, setNewVisitSource] = useState<JobAppointment | null>(null);
  // Every way into the new-visit sheet goes through here, so a plain new visit
  // never inherits the crew of the last one rescheduled as new.
  const openNewVisit = (fromVisit?: JobAppointment) => {
    setNewVisitSource(fromVisit ?? null);
    setNewVisitOpen(true);
  };
  const [schedulingVisit, setSchedulingVisit] = useState<JobAppointment | null>(null);
  const [schedulingCrewFocus, setSchedulingCrewFocus] = useState(false);
  // The length the visit has to cover when it was opened from a clash.
  const [schedulingDuration, setSchedulingDuration] = useState<number | undefined>();

  const {
    data: job,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiQuery<Job>({
    queryKey: jobKeys.detail(id),
    queryFn: async () => normalizeJob(await apiGet(operationsClient, jobEndpoints.detail(id))),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      ['DRAFT', 'SCHEDULED', 'IN_PROGRESS', 'ON_HOLD'].includes(
        normalizeLifecycleStatus(query.state.data?.statusPhase ?? query.state.data?.status),
      )
        ? 10_000
        : false,
  });

  const { data: pipelines = [], isLoading: pipelinesLoading } = useJobPipelines();
  const defaultPipeline = pipelines.find((pipeline) => pipeline.isDefault) ?? pipelines[0] ?? null;
  const pipelineId = job?.pipelineId ?? defaultPipeline?.id ?? null;
  const { data: pipeline, isLoading: pipelineLoading } = useJobPipeline(pipelineId);
  const appointmentsQuery = useJobAppointments(id);
  const appointments = appointmentsQuery.data ?? [];
  const canHoldThisJob =
    canScheduleJob && canHoldJob(jobEditScope, actorTechnicianId, appointments);
  const canRestoreThisJob =
    canScheduleJob && canRestoreJob(jobEditScope, actorTechnicianId, appointments);
  // NOT-360: resolve the job's assignee (a technician id) to a display name for the
  // rail Owner, and gather the relevant visit's full crew.
  const { data: schedulerTechnicians } = useSchedulerTechnicians();
  const { data: invoicesData } = useJobInvoices(job?.id ?? '', { limit: 100 });
  const scheduleJob = useScheduleJob();
  const invoices = invoicesData?.invoices ?? [];
  const invoiceCount = invoices.length;
  // The banners SHOW clearing bank money as paid: stored figures move only when
  // the bank settles. The outstanding amount is the sum of what each invoice
  // shows owing (credits included); a masked or withheld viewer gets the stored
  // balances.
  const clearingVisible = !pricingHidden && !paymentInfoHidden && !customerFinancialsHidden;
  const { balanceDue } = summarizeJobInvoiceClearing(invoices, clearingVisible);
  const pendingInvoiceCount = invoices.filter((invoice) => {
    if (invoiceCoveredByClearing(invoice, clearingVisible)) return false;
    const status = String(invoice.status ?? '').toLowerCase();
    return jobInvoiceBalance(invoice, clearingVisible) > 0 || UNPAID_INVOICE_STATUSES.has(status);
  }).length;
  const pipelineStages = sortPipelineStages(pipeline?.stages ?? []);
  const currentPhase = normalizePipelinePhase(job?.statusPhase ?? job?.status);
  const currentStage = job
    ? findCurrentPipelineStage(pipelineStages, currentPhase, job.status, job.stageId)
    : null;
  const isPipelineLoading = pipelinesLoading || pipelineLoading;
  const hasScheduledVisit = appointments.some(hasScheduledAppointment);

  function handleSelectAppointment(appointment: JobAppointment, options?: OpenVisitOptions) {
    setSchedulingVisit(appointment);
    setSchedulingCrewFocus(options?.crewFocus === true);
    setSchedulingDuration(options?.durationMinutes);
  }

  function handleCreateAppointment() {
    void router.navigate({
      to: '/dashboard/jobs/$id/appointments/new',
      params: { id },
    });
  }

  function handleScheduleNextStep() {
    // One-off pipeline only: advance Draft → Scheduled from DRAFT (once a visit
    // exists). Once the job is already SCHEDULED or later, just add another visit —
    // never fire a no-op SCHEDULED → SCHEDULED transition (the BE rejects it).
    if (job?.status === 'DRAFT' && hasScheduledVisit) {
      if (canScheduleJob) scheduleJob.mutate(id);
      return;
    }
    handleCreateAppointment();
  }

  function handleEditJob() {
    void router.navigate({ to: '/dashboard/jobs/$id/edit', params: { id } });
  }

  // NOT-297 Wave 2 — banner actions switch tabs via the page's existing
  // tab-navigation (preserves the rest of the search params).
  function handleSwitchTab(nextTab: JobTab) {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: { ...search, tab: nextTab },
      replace: true,
    });
  }

  function openExpenseDrawer(appointmentId?: string) {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        tab: 'costs',
        expenseDrawer: 'new',
        ...(appointmentId ? { appointmentId } : { appointmentId: undefined }),
      },
      replace: true,
    });
  }

  function closeExpenseDrawer() {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        expenseDrawer: undefined,
        appointmentId: undefined,
      },
      replace: true,
    });
  }

  function openInvoiceDrawer() {
    if (pricingHidden || billingReadOnly) return;
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        tab: 'billing',
        invoiceDrawer: 'new',
      },
      replace: true,
    });
  }

  function closeInvoiceDrawer() {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        invoiceDrawer: undefined,
      },
      replace: true,
    });
  }

  function openPaymentScheduleDrawer(mode: PaymentScheduleDrawerMode) {
    if (pricingHidden || billingReadOnly) return;
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        tab: 'billing',
        paymentScheduleDrawer: mode,
      },
      replace: true,
    });
  }

  function closePaymentScheduleDrawer() {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        ...search,
        paymentScheduleDrawer: undefined,
      },
      replace: true,
    });
  }

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  if (isLoading) {
    return <JobDetailSkeleton />;
  }

  if (isError || !job) {
    // NOT-297 Wave 4 — distinguish a missing/forbidden job (404/403) from a
    // generic load failure so the user gets the right recovery path: a "not
    // found / no access" message + back link vs. a retryable error.
    const status = error?.status;
    const notFoundOrForbidden = status === 404 || status === 403;
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 py-5">
        <Link
          to="/dashboard/jobs"
          search={listSearch}
          className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" />
          Jobs
        </Link>
        {notFoundOrForbidden ? (
          <SectionErrorState
            title={status === 403 ? 'No access to this job' : 'Job not found'}
            description={
              status === 403
                ? "You don't have permission to view this job. Check with an admin if you think this is a mistake."
                : "This job doesn't exist or may have been deleted."
            }
          />
        ) : (
          <SectionErrorState
            title="Couldn't load this job"
            description={error instanceof Error ? error.message : 'Please try again.'}
            onRetry={() => void refetch()}
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void router.navigate({ to: '/dashboard/jobs' })}
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to Jobs
        </Button>
      </div>
    );
  }

  const lifecycleStatus = normalizeLifecycleStatus(job.statusPhase ?? job.status);
  const isReadOnly = READ_ONLY_JOB_STATUSES.has(lifecycleStatus);
  // A completed job may receive an active follow-up visit. Keep its schedule
  // editable; individual completed/cancelled visits still enforce their locks.
  const visitsReadOnly = !canScheduleJob || (isReadOnly && lifecycleStatus !== 'COMPLETED');
  const canAddVisit =
    canCreateVisit && !['CANCELLED', 'CLOSED', 'ARCHIVED', 'INVOICED'].includes(lifecycleStatus);
  const billingReadOnly = isJobBillingReadOnly(lifecycleStatus);
  const headerActionsHidden = HEADER_ACTIONS_HIDDEN_STATUSES.has(lifecycleStatus);
  // NOT-339 (v4) — a recurring parent renders its own URL-synced tabbed shell
  // (Overview · Visits · Billing · Costs · Activity · Notes) with the identity
  // header + plan hero + rail; the one-off Visits/Finances/Notes tabs below are
  // skipped for it entirely.
  const isRecurring = job.bookingType === 'RECURRING';
  // A series would repeat the visit on every trip, so it never takes one. A job
  // sold on an estimate does once the estimate is approved; until then the
  // estimate is the job's scope and approving it rewrites the job's lines.
  const canAttachIncludedVisit =
    !isReadOnly && !isRecurring && INCLUDED_VISIT_ATTACHABLE_STATUSES.has(lifecycleStatus);
  const estimateAwaitsApproval =
    Boolean(job.estimateId) &&
    !['APPROVED', 'CONVERTED'].includes(job.sourceEstimate?.status ?? '');
  // Both shells now share the same tab set (Overview · Visits · Billing · Costs ·
  // Activity · Notes); coerce the `?tab=` param to it, defaulting to Overview. Legacy
  // finance deep links (finances/financials/payments/payment-schedule return links from
  // JobRowMenu, VisitAside, Create-invoice, etc.) map to the new Billing tab.
  const LEGACY_FINANCE_TABS = new Set(['finances', 'financials', 'payments', 'payment-schedule']);
  const requestedTab = search.tab && LEGACY_FINANCE_TABS.has(search.tab) ? 'billing' : search.tab;
  const allowedRequestedTab =
    (pricingHidden && requestedTab === 'billing') ||
    ((pricingHidden || profitabilityHidden) && requestedTab === 'costs') ||
    (!materialsEnabled && requestedTab === 'materials')
      ? 'overview'
      : requestedTab;
  const activeJobTab: RecurringJobTab =
    allowedRequestedTab && (RECURRING_JOB_TABS as readonly string[]).includes(allowedRequestedTab)
      ? (allowedRequestedTab as RecurringJobTab)
      : 'overview';

  // ── NOT-297 rail props ──────────────────────────────────────────────────────
  const nextAppointment = getNextScheduledAppointment(appointmentsQuery.data ?? []);
  // BR-77 — the earliest outstanding customer reschedule request across the
  // job's visits. The server sends `rescheduleRequest` only while it is live
  // (a move, cancel, completion or staff confirm retires it), so a request can
  // never linger here past the visit it was about.
  const pendingRescheduleRequest =
    (appointmentsQuery.data ?? [])
      .map((appointment) => getPendingRescheduleRequest(appointment))
      .filter((request): request is RescheduleRequest => request !== null)
      .sort((a, b) => (a.requestedAt < b.requestedAt ? -1 : 1))[0] ?? null;
  const railSourceEstimate =
    job.estimateId && job.sourceEstimate
      ? {
          id: job.sourceEstimate.id,
          estimateNumber: job.sourceEstimate.estimateNumber,
          search: DEFAULT_ESTIMATE_DETAIL_SEARCH,
        }
      : null;
  const railReview =
    job.signoff?.satisfactionRating != null
      ? {
          rating: job.signoff.satisfactionRating,
          notes: job.signoff.notes ?? null,
          collectedAt: job.signoff.collectedAt
            ? formatDate(job.signoff.collectedAt, companyTimeZone)
            : null,
        }
      : null;
  // `assignedTo` has existed as both Technician.id and Technician.authUserId across
  // job-owner flows. Resolve both shapes so a real owner never renders as unassigned.
  const railOwnerName =
    findTechnicianByAssignmentId(schedulerTechnicians, job.assignedTo)?.name ?? 'Unassigned';
  const railCrew = getRailCrew(appointmentsQuery.data ?? []);
  // The core job status behind the rail's stage label — null when the stage is
  // already named after its phase, so the rail never reads "Scheduled / Status:
  // Scheduled". With no pipeline at all the label IS the phase, so this is null
  // by the same rule.
  const railStageCoreStatus = stageCoreStatusLabel(
    currentStage ?? {
      name: formatLabel(lifecycleStatus),
      corePhase: lifecycleStatus,
    },
  );
  const railProps: JobDetailRailProps = {
    // Stage label from the current pipeline stage / phase (Wave 2 refines).
    stage: currentStage?.name ?? formatLabel(lifecycleStatus),
    // NOT-360: real job assignee (was hardcoded 'Unassigned').
    owner: railOwnerName,
    // NOT-360: the whole crew of the relevant visit (LEAD first, "+N" when 2+), not a
    // single technician name.
    tech: formatRailCrew(railCrew),
    nextVisit: nextAppointment?.startAt
      ? scheduleTz.format(
          nextAppointment.startAt,
          {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          },
          // Same visit as the card above; a mixed-zone job would otherwise show
          // the rail and the card on two different clocks in `service` mode.
          nextAppointment.siteTimezone,
        )
      : nextAppointment?.scheduledDate
        ? `${formatScheduledDay(nextAppointment.scheduledDate, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })} · Flexible day`
        : 'Not scheduled',
    workAuth: deriveGateState(job.workAuthorization?.required, job.authorization?.collectedAt),
    signoff: deriveGateState(job.completionSignoff?.required, job.signoff?.collectedAt),
    sourceEstimate: railSourceEstimate,
    createdBy: job.salesAttribution?.createdByRole
      ? formatLabel(job.salesAttribution.createdByRole)
      : '—',
    // TODO(NOT-297): needs the salesperson name on the job read DTO (only
    // salesAttribution.salespersonId is exposed). Until then show "—" — never a
    // flag/state like "Assigned" as the value.
    salesperson: '—',
    leadSource: job.salesAttribution?.leadSource
      ? formatLabel(job.salesAttribution.leadSource)
      : '—',
    review: railReview,
  };

  return (
    // Key by job id so the drawer stack resets when the same route instance moves
    // from one job to another (an open invoice/payment drawer must not survive onto
    // a different job).
    <BillingDetailDrawerProvider key={id}>
      <DetailPageLayout
        key={id}
        contentClassName="bg-muted/30"
        header={
          <JobDetailHeader
            job={job}
            readOnly={headerActionsHidden}
            onEdit={handleEditJob}
            onCreateVisit={
              canAddVisit
                ? isRecurring
                  ? handleCreateAppointment
                  : () => openNewVisit()
                : undefined
            }
            widthClassName={RECURRING_CONTENT_WIDTH}
          />
        }
        headerClassName="block border-0 p-0"
        rail={undefined}
      >
        {isRecurring ? (
          <RecurringJobDetail
            job={job}
            activeTab={activeJobTab}
            onTabChange={(nextTab) =>
              void router.navigate({
                to: '/dashboard/jobs/$id',
                params: { id },
                search: { ...search, tab: nextTab },
                replace: true,
              })
            }
            onSchedule={!isReadOnly && canCreateVisit ? handleCreateAppointment : undefined}
            readOnly={isReadOnly}
            pricingHidden={pricingHidden}
          />
        ) : (
          <OneOffJobDetail
            job={job}
            activeTab={activeJobTab}
            onTabChange={(nextTab) =>
              void router.navigate({
                to: '/dashboard/jobs/$id',
                params: { id },
                search: { ...search, tab: nextTab },
                replace: true,
              })
            }
            readOnly={isReadOnly}
            billingReadOnly={billingReadOnly}
            railStage={railProps.stage}
            railStageCoreStatus={railStageCoreStatus}
            railServiceType={job.jobType?.name ?? null}
            railNextVisit={railProps.nextVisit}
            railOwner={railProps.owner}
            railTech={railProps.tech}
            railCrewLabel={railCrew.length === 1 ? 'Tech' : 'Crew'}
            onOpenCustomer={
              job.contactId
                ? () =>
                    void router.navigate({
                      to: '/dashboard/contacts/$contactId',
                      params: { contactId: job.contactId! },
                      search: {
                        returnTo: `${window.location.pathname}${window.location.search}`,
                      },
                    })
                : undefined
            }
            onCreateExpense={!isReadOnly ? () => openExpenseDrawer() : undefined}
            onCreateInvoice={!billingReadOnly ? openInvoiceDrawer : undefined}
            onManagePaymentSchedule={
              !billingReadOnly ? (mode) => openPaymentScheduleDrawer(mode) : undefined
            }
            pricingHidden={pricingHidden}
            profitabilityHidden={profitabilityHidden}
            hero={
              <JobNextStepCard
                job={job}
                lifecycleStatus={lifecycleStatus}
                pipelineStages={pipelineStages}
                currentStage={currentStage}
                isPipelineLoading={isPipelineLoading}
                readOnly={isReadOnly}
                canManageLifecycle={canHoldThisJob}
                canHoldJob={canHoldThisJob}
                canRestoreJob={canRestoreThisJob}
                billingReadOnly={billingReadOnly}
                onSchedule={
                  !isReadOnly && (canScheduleJob || (!hasScheduledVisit && canCreateVisit))
                    ? handleScheduleNextStep
                    : undefined
                }
                onSwitchTab={handleSwitchTab}
                hasScheduledAppointment={hasScheduledVisit}
                isScheduleCheckLoading={appointmentsQuery.isLoading}
                isSchedulePending={scheduleJob.isPending}
                balanceDue={balanceDue}
                pendingInvoiceCount={pendingInvoiceCount}
                invoiceCount={invoiceCount}
                pricingHidden={pricingHidden}
                pendingRescheduleRequest={pendingRescheduleRequest}
                nextVisitConfirmation={nextAppointment?.confirmationStatus ?? null}
              />
            }
            visits={
              <>
                <DueIncludedVisitsCard
                  job={job}
                  enabled={canAttachIncludedVisit}
                  blockedReason={
                    estimateAwaitsApproval
                      ? "Available once this job's estimate is approved — approving it sets the job's lines."
                      : null
                  }
                />
                <JobAppointments
                  jobId={id}
                  jobNumber={job.jobNumber}
                  jobTypeName={job.jobType?.name}
                  serviceAddress={job.serviceAddress ?? null}
                  jobLineItems={job.lineItems}
                  bookingType={job.bookingType}
                  onSelectAppointment={handleSelectAppointment}
                  canCreateAppointment={canAddVisit}
                  onCreateAppointment={canAddVisit ? openNewVisit : undefined}
                  readOnly={visitsReadOnly}
                  requiredMinutes={requiredMinutesForAddedWork(job.membershipContext)}
                />
              </>
            }
          />
        )}
        {activeJobTab === 'overview' ? (
          <div className={cn('mt-5 space-y-5', RECURRING_CONTENT_WIDTH)}>
            {/* Answers Iris collected during intake (renders only when present). */}
            <JobIrisSurveyCard job={job} />
          </div>
        ) : null}
        {activeJobTab === 'materials' && (
          <div className={RECURRING_CONTENT_WIDTH}>
            {/* How the job gets its materials: stock, suppliers or the technician. */}
            <JobMaterialsTab job={job} />
          </div>
        )}
        {activeJobTab === 'equipment' && (
          <div className={RECURRING_CONTENT_WIDTH}>
            <JobEquipmentSection
              jobId={job.id}
              customerId={job.contactId}
              serviceAddressId={job.serviceAddress?.addressId ?? null}
              canEdit={
                !isReadOnly || lifecycleStatus === 'COMPLETED' || lifecycleStatus === 'INVOICED'
              }
            />
          </div>
        )}
        {!isRecurring && (
          <>
            <NewVisitDrawer
              canSchedule={canScheduleJob}
              job={job}
              open={newVisitOpen}
              onOpenChange={setNewVisitOpen}
              copyFrom={newVisitSource}
              onScheduled={() => {
                // Only job editors may advance Draft → Scheduled. A technician
                // with jobs:create_visit still completes the visit creation,
                // while the job lifecycle remains administrator-controlled.
                if (job.status === 'DRAFT' && canScheduleJob) scheduleJob.mutate(id);
              }}
            />
            <NewExpenseDrawer
              jobId={job.id}
              appointmentId={search.appointmentId}
              open={search.expenseDrawer === 'new'}
              onOpenChange={(open) => {
                if (!open) closeExpenseDrawer();
              }}
            />
            <NewInvoiceDrawer
              jobId={job.id}
              open={!pricingHidden && !billingReadOnly && search.invoiceDrawer === 'new'}
              onOpenChange={(open) => {
                if (!open) closeInvoiceDrawer();
              }}
            />
            <PaymentScheduleDrawer
              jobId={job.id}
              open={
                !pricingHidden &&
                !billingReadOnly &&
                (search.paymentScheduleDrawer === 'new' || search.paymentScheduleDrawer === 'edit')
              }
              onOpenChange={(open) => {
                if (!open) closePaymentScheduleDrawer();
              }}
            />
          </>
        )}
        {schedulingVisit && (
          <VisitSchedulingPanel
            open
            onClose={() => setSchedulingVisit(null)}
            jobId={job.id}
            job={job}
            appointment={schedulingVisit}
            crewFocus={schedulingCrewFocus}
            initialDurationMinutes={schedulingDuration}
            readOnly={visitsReadOnly}
          />
        )}
      </DetailPageLayout>
    </BillingDetailDrawerProvider>
  );
}
